import {
  AccessTokens,
  AccessTokenVariants,
  ActualProtectionTokenFragment,
  AllTokens,
  ContextProtectionTokens,
  EnabledTokens,
} from "gql/graphql";
import { ContextTokens } from "@/shared/security";
import { useUserProtectionTokens } from "@/shared/security/model/user-protection-tokens/use-user-protection-tokens";

const NONE = 0;
const READ = 1;
const MODIFY = 2;

export function useAbility(influenceTokens: AllTokens[]) {
  const tokenToLevel = useTokenToLevelFn();

  const ability = influenceTokens.reduce(
    (result, token) => Math.max(result, tokenToLevel(token)),
    NONE,
  );

  return toPermissionFlags(ability);
}

/**
 * Уровень права: сначала значение напротив открытой записи, потом глобальный снимок. Значения
 * приходят посчитанными — границы правил применил сервер, поэтому ни выключатель в уровень,
 * ни «своё/чужое» из пары кодов клиент больше не разворачивает.
 *
 * Отвечает строго про уровень. Право с контекстами вне карточки записи уровня не имеет, и
 * подставлять вместо него `anyAccess` нельзя: признак говорит лишь «где-то что-то открыто».
 */
export function useTokenToLevelFn() {
  const { tokens: userTokens } = useUserProtectionTokens();
  const recordTokens = ContextTokens.useContextTokens();

  function tokenToLevel(token: AllTokens) {
    const recordLevel = recordTokenToLevel(recordTokens, token);

    if (recordLevel !== null) {
      return recordLevel;
    }

    const userToken = userTokens.find((userToken) => {
      if (userToken.__typename === "AccessProtectionTokenDto") {
        return userToken.accessToken === (token as unknown as AccessTokens);
      }

      if (userToken.__typename === "EnabledProtectionTokenDto") {
        return userToken.enabledToken === (token as unknown as EnabledTokens);
      }

      return (
        userToken.contextToken === (token as unknown as ContextProtectionTokens)
      );
    });

    if (userToken?.__typename === "AccessProtectionTokenDto") {
      return accessToLevel(userToken.access);
    }

    if (userToken?.__typename === "EnabledProtectionTokenDto") {
      return userToken.enabled ? MODIFY : NONE;
    }

    /**
     * Право с контекстами вне карточки записи уровня не имеет: снимок отвечает по нему только
     * `anyAccess` — «есть ли доступ хоть к кому-нибудь», — а это не уровень и сравнивать его с
     * READ или MODIFY нельзя. Здесь такое право не разрешает ничего; вопрос «показывать ли
     * интерфейс вовсе» задают отдельно, через `useHasAnyAccess`.
     */
    return NONE;
  }

  return tokenToLevel;
}

function recordTokenToLevel(
  recordTokens: ActualProtectionTokenFragment[],
  token: AllTokens,
) {
  // Идём с конца: вложенный провайдер дописывает свои значения после внешних, и
  // побеждать должна более конкретная запись, а не та, что обёрнута снаружи.
  const recordToken = [...recordTokens]
    .reverse()
    .find(
      (recordToken) =>
        recordToken.token === (token as unknown as ContextProtectionTokens),
    );

  if (!recordToken) {
    return null;
  }

  if (recordToken.__typename === "ActualAccessProtectionTokenDto") {
    return accessToLevel(recordToken.access);
  }

  return recordToken.enabled ? MODIFY : NONE;
}

function accessToLevel(access: AccessTokenVariants) {
  if (access === AccessTokenVariants.Modify) {
    return MODIFY;
  }

  return access === AccessTokenVariants.Read ? READ : NONE;
}

export function toPermissionFlags(ability: number) {
  return {
    canRead: ability >= READ,
    canModify: ability >= MODIFY,
    notAvailable: ability === NONE,
  };
}
